import { ActionIcon, Badge, Group, Modal, Text, Title, Tooltip } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useOutlet } from 'react-router';

import { useCatalogLabels } from '../labels';
import { DETAIL_OVERLAY_Z_INDEX, OVERLAY_DROPDOWN_Z_INDEX } from './headerLayout';
import { useIsMobile } from './mobileLayout';
import { ProductImage } from './ProductImage';
import { WishlistButton } from './WishlistButton';

// A detail view - an article or a document - laid OVER the list it was opened from instead of
// replacing it with a page of its own (see chat: "Wenn wir einen Artikel öffnen, ist das immer ein
// neuer 'leerer' Layer … Können wir den aufrufenden Layer im Hintergrund sichtbar lassen (grau)?",
// and for the PDF viewer: "Auch dieses Fenster wirkt 'aus dem Kontext gerissen'").
//
// Mechanically the article is a CHILD ROUTE of the page it opens over - `/catalog/article/:artNr`
// under CatalogPage, `/catalog/wishlist/article/:artNr` under WishlistPage (see AppRoutes.tsx).
// React Router therefore keeps the PARENT mounted and hands the article in through `useOutlet()`,
// which buys three things at once:
//   - the context is never lost (see chat: "Ein Artikel kann aufgerufen werden aus der Suche, als
//     Link von einem System … Wir dürfen den 'Kontext' nicht verlieren"): whatever the parent was
//     showing - a search result list, a system's Artikelliste, the Merkliste - is still behind the
//     overlay, with its scroll position, filters and loaded data intact, because nothing unmounted;
//   - no refetch on open OR on close, since the parent never rebuilt;
//   - the URL stays shareable, and opening it cold simply renders the parent underneath as well.
// That is also why the links leading here are RELATIVE (`article/<artNr>`, see ProductGrid /
// DocumentSearchResults): the same grid resolves to the right parent in either place.
//
// The overlay's header carries the object it is showing - thumbnail, name, article number - and
// two equally weighted, quiet actions (see design review, point 5). It used to be empty apart from
// those actions, ~62px of nothing with a solid blue "Schließen" button as the loudest thing on the
// layer, while the title scrolled away in the body below. Note this reverses only the LAYOUT of the
// earlier "der Artikellayer hat dann auch keinen 'Header'" decision, not its intent: the point was
// that the overlay must not carry a second app chrome (logo bar, navigation, "Zurück zum Katalog"),
// and it still doesn't - what sits there now is the object's own identity, which is exactly what
// keeps the layer from reading as context-free.

/** Below this width the overlay goes full screen - a centred dialog with a grey frame around it is
 * wasted space on a phone, and the article's own two-column tabs need every pixel (see chat: "Muss
 * auch mobil funktionieren"). */
const FULLSCREEN_BELOW = '(max-width: 62em)';

/** How much of the page behind stays uncovered at the top on desktop (see chat: "Das overlay darf
 * auf desktop bildschirmen etwas tiefer sein (Hintergrund oben sichtbar). Vielleicht 60px") - it
 * makes the overlay read as a layer ON the list rather than as a new page. */
const DESKTOP_TOP_GAP = 60;
/** Breathing room left below the overlay, so it doesn't touch the bottom edge either. */
const DESKTOP_BOTTOM_GAP = 24;

/**
 * How wide the layer gets.
 *
 * Both contents earn a wide layer - a PDF and a usage table on one side, wide attribute and
 * document tables on the other (see chat: "Bei Dokumenten kannst Du über die Breite gehen" - "gilt
 * auch für Artikel"). But the previous `min(1800px, 96vw)` left roughly 38px of the page showing on
 * a 1900px monitor, which defeated the layer's whole purpose: you could no longer see where you
 * came from, so it read as a half-hearted full-screen page rather than as something laid on top
 * (see design review, point 5). 88vw leaves a real margin at every window size, and the lower cap
 * keeps a line of table text from running absurdly wide on an ultra-wide monitor.
 */
const WIDTH = 'min(1440px, 88vw)';

/**
 * What the overlay's header shows. Deliberately PRIMITIVES only, no ReactNode: the child route
 * hands this up through an effect, and a node would be a fresh object on every render, so the
 * effect would loop. The overlay renders the thumbnail and the Merkliste toggle itself from these.
 */
export interface DetailOverlayHeaderInfo {
    /** The object's name - the layer's own heading. */
    title: string;
    /** One quiet line under it, e.g. "Artikelnummer: 189701000". */
    subtitle?: string;
    imageSrc?: string | null;
    imageFallbackSrc?: string | null;
    /** Article number, when this object can go on the Merkliste - renders the toggle in the header. */
    wishlistArtNr?: string;
    /** Short qualifier shown as a grey pill left of the actions, e.g. "Teil". */
    badge?: string;
}

/** `undefined` outside an overlay - a detail page rendered as a plain route then simply keeps its
 * own in-body header instead of pushing it somewhere that isn't listening. */
const DetailOverlayHeaderContext = createContext<((info?: DetailOverlayHeaderInfo) => void) | undefined>(undefined);

/**
 * Lets the route inside the overlay put its object into the overlay's header.
 *
 * Pass `undefined` while the data is still loading; the header then shows nothing rather than a
 * placeholder that shifts as soon as the real title arrives. Every dependency below is a primitive,
 * so this settles after one run per article.
 */
export const useDetailOverlayHeader = (info?: DetailOverlayHeaderInfo) => {
    const setHeader = useContext(DetailOverlayHeaderContext);
    const { title, subtitle, imageSrc, imageFallbackSrc, wishlistArtNr, badge } = info ?? {};

    useEffect(() => {
        if (!setHeader) {
            return undefined;
        }
        setHeader(title ? { title, subtitle, imageSrc, imageFallbackSrc, wishlistArtNr, badge } : undefined);
        // Cleared on unmount so the header cannot outlive its content - e.g. when the user steps
        // from an article straight to a document, or closes the layer mid-load.
        return () => setHeader(undefined);
    }, [setHeader, title, subtitle, imageSrc, imageFallbackSrc, wishlistArtNr, badge]);
};

/**
 * Stops the page BEHIND the overlay from scrolling on its own, so the layer shows one scrollbar
 * (its body's) instead of two side by side - one of the strongest "unrund" cues there is, because
 * the user cannot tell which of the two the wheel will move (see design review, point 6).
 *
 * Mantine's <Modal> already locks scrolling, but it locks `document.body` - and this app never
 * scrolls there: App.css puts `height: 100%` on `html`, `body` AND `#root`, so the content
 * overflows all the way up and it is the ROOT element that ends up scrolling. Locking body is then
 * a no-op, which is why the second bar survived.
 *
 * Deliberately conservative, because this sandbox has no access to the private @uag/react-core
 * BaseLayout to confirm where the scroll actually lands: it only acts when the root element is
 * genuinely scrollable at the moment the overlay opens, and it compensates for the scrollbar it
 * removes so the page behind does not jump sideways. If the real scroll container turns out to be
 * some element inside BaseLayout instead, this is simply inert rather than wrong.
 */
const useLockRootScroll = (locked: boolean) => {
    useEffect(() => {
        const root = document.documentElement;
        if (!locked || root.scrollHeight <= root.clientHeight) {
            return undefined;
        }
        const scrollbarWidth = window.innerWidth - root.clientWidth;
        const previousOverflow = root.style.overflow;
        const previousPaddingRight = root.style.paddingRight;

        root.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
            root.style.paddingRight = `${scrollbarWidth}px`;
        }

        return () => {
            root.style.overflow = previousOverflow;
            root.style.paddingRight = previousPaddingRight;
        };
    }, [locked]);
};

/**
 * Closes the overlay by going back to wherever the article was opened from - the list, the
 * Merkliste, the landing page's search suggestions. History rather than a hard-coded path is what
 * keeps that context: only a deep link (the article URL opened cold, nothing behind it) falls back
 * to `fallbackPath`.
 */
export const useCloseDetailOverlay = (fallbackPath: string) => {
    const navigate = useNavigate();

    return () => {
        const historyIndex = (window.history.state as { idx?: number } | null)?.idx;
        if (typeof historyIndex === 'number' && historyIndex > 0) {
            navigate(-1);
            return;
        }
        navigate(fallbackPath);
    };
};

export const DetailOverlay = ({ content, onClose }: { content: ReactNode; onClose: () => void }) => {
    const { L } = useCatalogLabels();
    const [maximized, setMaximized] = useState(false);
    // The child route fills this through useDetailOverlayHeader. `setHeader` comes straight from
    // useState, so its identity is stable and the child's effect has nothing to re-run on.
    const [header, setHeader] = useState<DetailOverlayHeaderInfo | undefined>(undefined);
    // `getInitialValueInEffect: false` evaluates the query on the FIRST render instead of after an
    // effect - otherwise a phone briefly gets the centred desktop dialog before it snaps to full
    // screen.
    const isSmallScreen = useMediaQuery(FULLSCREEN_BELOW, false, { getInitialValueInEffect: false });
    const isPhone = useIsMobile();
    // On a phone it is full screen anyway, so the toggle would be a no-op there.
    const isFullScreen = isSmallScreen || maximized;

    useLockRootScroll(Boolean(content));

    return (
        <Modal.Root
            fullScreen={isFullScreen}
            opened={Boolean(content)}
            // Tighter on a phone: every pixel of the 360-400 it has is content width.
            padding={isPhone ? 'sm' : 'lg'}
            radius={isFullScreen ? 0 : 'md'}
            size={WIDTH}
            /*
             * EVERYTHING layout-related goes through `styles`, i.e. Mantine's own Styles API, and
             * NOT through a `style` prop on <Modal.Content>. That was the bug behind "der Layer
             * geht über die ganze Breite, die Breite wird aber nicht genutzt": <Modal.Content>
             * renders the positioning wrapper (`.mantine-Modal-inner`) AND the dialog itself, and a
             * `style` handed to it landed on the WRAPPER. `flexDirection: 'column'` on that wrapper
             * turns its cross axis horizontal, so `align-items: flex-start` stopped stretching the
             * dialog sideways and `flex: 0 0 var(--modal-size)` - the rule that is supposed to set
             * the WIDTH - was read as a height instead. The wrapper filled the window while the
             * dialog shrank to its content (confirmed in the DevTools screenshot: inner 1536x140).
             *
             * `styles` targets each element by name, so the wrapper keeps Mantine's own row layout.
             */
            styles={{
                // Top-aligned with a gap instead of vertically centred: the strip of the list
                // showing above the overlay is the whole point.
                inner: isFullScreen
                    ? undefined
                    : { paddingTop: DESKTOP_TOP_GAP, paddingBottom: DESKTOP_BOTTOM_GAP, alignItems: 'flex-start' },
                // A FIXED height, not just a cap (see chat: "voll höhe mit rand unten") - letting it
                // shrink to its content made the layer jump between tabs and left the PDF viewer a
                // letterbox. The column layout is what lets the body hand its height down to the
                // viewer. Rounded on all four corners and clipped to that radius, because header and
                // body paint their own opaque, square backgrounds over the corners.
                content: {
                    height: isFullScreen ? '100dvh' : `calc(100vh - ${DESKTOP_TOP_GAP + DESKTOP_BOTTOM_GAP}px)`,
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: isFullScreen ? 0 : 'var(--mantine-radius-md)',
                    overflow: 'hidden'
                },
                // The header now carries the object itself, so it needs the full row rather than
                // the flex-end that used to push two buttons into an otherwise empty bar. A hairline
                // separates it from the scrolling body beneath, and a smaller vertical padding keeps
                // it to the height its content actually needs.
                header: {
                    alignItems: 'center',
                    gap: 'var(--mantine-spacing-md)',
                    paddingTop: 'var(--mantine-spacing-sm)',
                    paddingBottom: 'var(--mantine-spacing-sm)',
                    borderBottom: '1px solid var(--mantine-color-default-border)'
                },
                // The scroll container is the body, not the whole dialog: the header stays put, and
                // `minHeight: 0` is what allows a flex child (the PDF viewer) to size itself to the
                // remaining space instead of overflowing it.
                body: { flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }
            }}
            zIndex={DETAIL_OVERLAY_Z_INDEX}
            onClose={onClose}
        >
            {/* The page behind stays readable - hence a moderate opacity and just a touch of blur,
                not the near-opaque default: being able to see where you came from is the point. */}
            <Modal.Overlay backgroundOpacity={0.45} blur={2} />
            <Modal.Content>
                <Modal.Header>
                    {header && (
                        <>
                            {/* Same box treatment as every other thumbnail in the app (see
                                ProductImage) - explicit width=height, otherwise it stretches to
                                whatever the header row leaves it. */}
                            <ProductImage
                                alt={header.title}
                                fallbackSrc={header.imageFallbackSrc}
                                height={36}
                                src={header.imageSrc}
                                width={36}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                {/* order={1} so the heading scale tints it in the brand blue (see
                                    getCustomThemeOverrides); `size` brings it back down to a
                                    header-bar size without changing the document outline - the
                                    object's name IS this layer's top heading. */}
                                {/* On a phone the name gets two lines instead of one clipped to
                                    "Doka-…", at a size that fits them - set inline, which is what
                                    reliably beats the theme's own heading rules. A long name still
                                    ends in an ellipsis after the second line, and the desktop bar
                                    keeps its single line. */}
                                <Title
                                    lineClamp={isPhone ? 2 : 1}
                                    order={1}
                                    size="h4"
                                    style={{
                                        minWidth: 0,
                                        overflowWrap: 'anywhere',
                                        ...(isPhone ? { fontSize: '1.0625rem', lineHeight: 1.25 } : undefined)
                                    }}
                                >
                                    {header.title}
                                </Title>
                                {header.subtitle && (
                                    <Text c="dimmed" size="sm" truncate>
                                        {header.subtitle}
                                    </Text>
                                )}
                            </div>
                            {header.badge && (
                                <Badge color="gray" size="lg" variant="light">
                                    {header.badge}
                                </Badge>
                            )}
                            {header.wishlistArtNr && (
                                <WishlistButton id={header.wishlistArtNr} size={20} type="product" />
                            )}
                        </>
                    )}
                    {/* Both actions carry the same weight, because neither is the thing the user
                        came here to do. "Schließen" used to be a filled primary button - the single
                        loudest element on the layer, for its least important action. */}
                    <Group gap={4} wrap="nowrap">
                        {/* Hidden on a phone: there the overlay already fills the screen. The
                            tooltip is portaled like every Mantine overlay - without the explicit
                            z-index it would render behind the modal it belongs to (see
                            OVERLAY_DROPDOWN_Z_INDEX). */}
                        {!isSmallScreen && (
                            <Tooltip label={maximized ? L.restore : L.maximize} zIndex={OVERLAY_DROPDOWN_Z_INDEX} withArrow>
                                <ActionIcon
                                    aria-label={maximized ? L.restore : L.maximize}
                                    size="lg"
                                    variant="subtle"
                                    onClick={() => setMaximized((prev) => !prev)}
                                >
                                    <span className="material-symbols-outlined" style={{ fontSize: 20 }} aria-hidden>
                                        {maximized ? 'close_fullscreen' : 'open_in_full'}
                                    </span>
                                </ActionIcon>
                            </Tooltip>
                        )}
                        <Tooltip label={L.close} zIndex={OVERLAY_DROPDOWN_Z_INDEX} withArrow>
                            <ActionIcon aria-label={L.close} size="lg" variant="subtle" onClick={onClose}>
                                <span className="material-symbols-outlined" style={{ fontSize: 20 }} aria-hidden>
                                    close
                                </span>
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                </Modal.Header>
                <Modal.Body>
                    <DetailOverlayHeaderContext.Provider value={setHeader}>
                        {content}
                    </DetailOverlayHeaderContext.Provider>
                </Modal.Body>
            </Modal.Content>
        </Modal.Root>
    );
};

/**
 * Drop-in for a page that can host the article overlay: renders nothing while the plain page is
 * showing, the overlay as soon as the article child route matches.
 */
export const DetailOutlet = ({ fallbackPath }: { fallbackPath: string }) => {
    const outlet = useOutlet();
    const close = useCloseDetailOverlay(fallbackPath);

    return outlet ? <DetailOverlay content={outlet} onClose={close} /> : null;
};
